"use client";
import React, { useEffect, useRef } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { HowToVote as VoteIcon } from "@mui/icons-material";
import { safeT } from "@/utils/safeT";
import { useTranslations } from "next-intl";

export interface CelebrationIconButtonProps {
  onClick: () => void;
  fire: boolean;
  onAnimationEnd: () => void;
  colors?: string[] | string;
  particleCount?: number;
  maxDistance?: number;
  animationTime?: number;
  sx?: SxProps<Theme>;
  fireAlign?: "left" | "center" | "right";
}

const CelebrationIconButton = ({
  onClick,
  fire,
  onAnimationEnd,
  colors = ["#ff5252", "#ffb142", "#1e90ff", "#2ed573"],
  particleCount = 20,
  maxDistance = 30,
  animationTime = 1200,
  sx,
  fireAlign = "center",
}: CelebrationIconButtonProps) => {
  const colorArray = Array.isArray(colors) ? colors : [colors];
  const animationTimeoutRef = useRef<number>(animationTime);
  const t = useTranslations();

  useEffect(() => {
    if (fire) {
      animationTimeoutRef.current = window.setTimeout(() => {
        onAnimationEnd();
      }, animationTime);
    }

    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [fire, onAnimationEnd]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onClick();
  };

  // 방향별 가중치에 따른 angle 범위 선택 함수
  const getWeightedAngle = () => {
    const rand = Math.random();

    if (fireAlign === "left") {
      if (rand < 0.5) return (Math.random() * 120 - 60) * (Math.PI / 180); // -60° ~ 60°
      if (rand < 0.8) return (Math.random() * 120 + 60) * (Math.PI / 180); // 60° ~ 180°
      return (Math.random() * 120 + 180) * (Math.PI / 180); // 180° ~ 300°
    }

    if (fireAlign === "right") {
      if (rand < 0.5) return (Math.random() * 120 + 120) * (Math.PI / 180); // 120° ~ 240°
      if (rand < 0.9) return (Math.random() * 120 + 240) * (Math.PI / 180); // 240° ~ 360°
      return Math.random() * 120 * (Math.PI / 180); // 0° ~ 120°
    }

    // center: 균등 분포
    return Math.random() * Math.PI * 2;
  };

  return (
    <Box position="relative" display="inline-flex" alignItems="center" sx={{ overflow: "visible" }}>
      <IconButton
        component="span"
        onClick={handleClick}
        aria-label={safeT(t, "ui.label.vote", {
          defaultMessage: "투표",
        })}
        sx={{
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          backdropFilter: "blur(1.5px)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          color: "#FFFFFF",
          borderRadius: "8px",
          px: 1.5,
          py: 0.5,
          boxShadow: 2,
          "&:hover": { bgcolor: "rgba(0, 0, 0, 0.4)" },
          display: "flex",
          flexDirection: "row",
          gap: 0.5,
          height: 32,
          ...sx,
        }}
      >
        <VoteIcon fontSize="small" />
        <Typography variant="button" noWrap sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
          {safeT(t, "ui.label.vote", {
            defaultMessage: "투표",
          })}
        </Typography>
      </IconButton>

      {/* 🎆 Firework 애니메이션 */}
      {fire && (
        <Box
          position="absolute"
          top="50%"
          left="50%"
          sx={{ width: 0, height: 0, overflow: "visible", pointerEvents: "none" }}
        >
          {Array.from({ length: particleCount }).map((_, i) => {
            const angle = getWeightedAngle();
            const distance = Math.random() * maxDistance + 15;
            const size = Math.random() * 6 + 4;
            const delay = Math.random() * 0.3;
            const duration = Math.random() * 0.6 + 0.8;
            const bg = colorArray[Math.floor(Math.random() * colorArray.length)];

            return (
              <Box
                key={i}
                component="span"
                position="absolute"
                top={0}
                left={0}
                sx={{
                  width: size,
                  height: size,
                  borderRadius: "50%",
                  backgroundColor: bg,
                  transform: `translate(-50%, -50%)`,
                  animation: `firework ${duration}s ease-out forwards`,
                  animationDelay: `${delay}s`,
                  "--dx": `${Math.cos(angle) * distance}px`,
                  "--dy": `${Math.sin(angle) * distance}px`,
                }}
              />
            );
          })}
        </Box>
      )}

      {/* Keyframes 정의 */}
      <style>
        {`
        @keyframes firework {
          0% {
            transform: translate(-50%, -50%) scale(0.2);
            opacity: 1;
          }
          100% {
            transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(1);
            opacity: 0;
          }
        }
      `}
      </style>
    </Box>
  );
};

export default CelebrationIconButton;
